import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ErrorService } from '../shared/services/error.service';

export interface TransactionServiceOrder {
  id: number;
  configOrderId: number | null;
  firstStatusId: number | null;
  statusDirectionId: number | null;
  groupId: number | null;
  firstStatusName?: string | null;
  statusDirectionName?: string | null;
  /** @deprecated Compatibilidade com backends antigos que ainda retornam DepartmentId */
  departmentId?: number | null;
}

export interface TransactionServiceOrderCreate {
  firstStatusId: number;
  statusDirectionId: number;
  groupId: number;
  configOrderId?: number;
  /** @deprecated Preferir groupId */
  departmentId?: number;
}

export interface TransactionServiceOrderUpdate {
  firstStatusId?: number;
  statusDirectionId?: number;
  groupId?: number;
  configOrderId?: number;
  /** @deprecated Preferir groupId */
  departmentId?: number;
}

@Injectable({ providedIn: 'root' })
export class TransactionServiceOrdersService {
  private readonly baseUrl = environment.microservices?.order || environment.apiUrl || '/api';
  private readonly resourcePath = 'transactionserviceorders';

  constructor(
    private http: HttpClient,
    private errorService: ErrorService
  ) {}

  private getHeaders(): HttpHeaders {
    const token = localStorage.getItem('authToken') || localStorage.getItem('token');
    const headerMap: Record<string, string> = {
      'Content-Type': 'application/json'
    };

    if (token) {
      headerMap['Authorization'] = `Bearer ${token}`;
    }

    return new HttpHeaders(headerMap);
  }

  private buildUrl(path: string): string {
    const [base, baseQuery] = String(this.baseUrl).split('?');
    const normalizedBase = base.replace(/\/+$/, '');
    const normalizedPath = String(path).replace(/^\/+/, '');
    let url = `${normalizedBase}/${normalizedPath}`;
    if (baseQuery) {
      url += `?${baseQuery}`;
    }
    return url;
  }

  /**
   * GET /transactionserviceorders
   */
  getAll(): Observable<TransactionServiceOrder[]> {
    return this.http.get<any>(this.buildUrl(this.resourcePath), { headers: this.getHeaders() }).pipe(
      map(res => this.extractArray(res).map(dto => this.mapFromDto(dto))),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao listar regras de transação de OS');
        return throwError(() => err);
      })
    );
  }

  /**
   * GET /transactionserviceorders/{id}
   */
  getById(id: number): Observable<TransactionServiceOrder> {
    return this.http.get<any>(this.buildUrl(`${this.resourcePath}/${id}`), { headers: this.getHeaders() }).pipe(
      map(res => this.mapFromDto(this.extractItem(res))),
      catchError(err => {
        if (err?.status !== 404) {
          this.errorService.showErrorMessage('Erro ao buscar regra de transação de OS');
        }
        return throwError(() => err);
      })
    );
  }

  /**
   * GET /transactionserviceorders?firstStatusId={firstStatusId}
   */
  getByFirstStatus(firstStatusId: number): Observable<TransactionServiceOrder[]> {
    const params = new HttpParams().set('firstStatusId', String(firstStatusId));
    return this.http.get<any>(this.buildUrl(this.resourcePath), { headers: this.getHeaders(), params }).pipe(
      map(res => this.extractArray(res).map(dto => this.mapFromDto(dto))),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar regras por status inicial');
        return throwError(() => err);
      })
    );
  }

  /**
   * GET /transactionserviceorders?statusDirectionId={statusDirectionId}
   */
  getByStatusDirection(statusDirectionId: number): Observable<TransactionServiceOrder[]> {
    const params = new HttpParams().set('statusDirectionId', String(statusDirectionId));
    return this.http.get<any>(this.buildUrl(this.resourcePath), { headers: this.getHeaders(), params }).pipe(
      map(res => this.extractArray(res).map(dto => this.mapFromDto(dto))),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar regras por direcionamento');
        return throwError(() => err);
      })
    );
  }

  /**
   * GET /transactionserviceorders?groupId={groupId}
   */
  getByGroup(groupId: number): Observable<TransactionServiceOrder[]> {
    const params = new HttpParams().set('groupId', String(groupId));
    return this.http.get<any>(this.buildUrl(this.resourcePath), { headers: this.getHeaders(), params }).pipe(
      map(res => this.extractArray(res).map(dto => this.mapFromDto(dto))),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar regras por grupo');
        return throwError(() => err);
      })
    );
  }

  /**
   * GET /transactionserviceorders?configOrderId={configOrderId}
   */
  getByConfigOrderId(configOrderId: number): Observable<TransactionServiceOrder[]> {
    const params = new HttpParams().set('configOrderId', String(configOrderId));
    return this.http.get<any>(this.buildUrl(this.resourcePath), { headers: this.getHeaders(), params }).pipe(
      map(res => this.extractArray(res).map(dto => this.mapFromDto(dto))),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar regras por configuração de OS');
        return throwError(() => err);
      })
    );
  }

  /**
   * @deprecated O backend atual usa groupId. Mantido para compatibilidade.
   * GET /transactionserviceorders/department/{departmentId}
   */
  getByDepartment(departmentId: number): Observable<TransactionServiceOrder[]> {
    return this.http.get<any>(this.buildUrl(`${this.resourcePath}/department/${departmentId}`), { headers: this.getHeaders() }).pipe(
      map(res => this.extractArray(res).map(dto => this.mapFromDto(dto))),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar regras por departamento');
        return throwError(() => err);
      })
    );
  }

  /**
   * GET /transactionserviceorders/rule?firstStatusId=...&statusDirectionId=...
   */
  getRule(firstStatusId: number, statusDirectionId: number): Observable<TransactionServiceOrder> {
    const params = new HttpParams()
      .set('firstStatusId', String(firstStatusId))
      .set('statusDirectionId', String(statusDirectionId));

    return this.http.get<any>(this.buildUrl(`${this.resourcePath}/rule`), { headers: this.getHeaders(), params }).pipe(
      map(res => this.mapFromDto(this.extractItem(res))),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar regra (status inicial + direcionamento)');
        return throwError(() => err);
      })
    );
  }

  /**
   * POST /transactionserviceorders
   * Observação: alguns backends aceitam configOrderId no body.
   */
  create(payload: TransactionServiceOrderCreate): Observable<TransactionServiceOrder> {
    const headers = this.getHeaders();

    return this.http.post<any>(this.buildUrl(this.resourcePath), this.normalizePayload(payload), { headers }).pipe(
      map(res => this.mapFromDto(this.extractItem(res))),
      tap(() => this.errorService.showSuccessMessage('Regra de transação criada com sucesso')),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao criar regra de transação');
        return throwError(() => err);
      })
    );
  }

  /**
   * PUT /transactionserviceorders/{id}
   */
  update(id: number, payload: TransactionServiceOrderUpdate): Observable<TransactionServiceOrder> {
    const headers = this.getHeaders();

    return this.http.put<any>(this.buildUrl(`${this.resourcePath}/${id}`), this.normalizePayload(payload), { headers }).pipe(
      map(res => this.mapFromDto(this.extractItem(res))),
      tap(() => this.errorService.showSuccessMessage('Regra de transação atualizada com sucesso')),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao atualizar regra de transação');
        return throwError(() => err);
      })
    );
  }

  /**
   * DELETE /transactionserviceorders/{id}
   */
  delete(id: number): Observable<void> {
    return this.http.delete<void>(this.buildUrl(`${this.resourcePath}/${id}`), { headers: this.getHeaders() }).pipe(
      map(() => void 0),
      tap(() => this.errorService.showSuccessMessage('Regra de transação removida com sucesso')),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao remover regra de transação');
        return throwError(() => err);
      })
    );
  }

  private extractArray(response: any): any[] {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    if (Array.isArray(response?.data?.data)) return response.data.data;
    if (Array.isArray(response?.items)) return response.items;
    if (Array.isArray(response?.data?.items)) return response.data.items;
    if (Array.isArray(response?.transactionServiceOrders)) return response.transactionServiceOrders;
    if (Array.isArray(response?.data?.transactionServiceOrders)) return response.data.transactionServiceOrders;
    return [];
  }

  private extractItem(response: any): any {
    return response?.data?.data ?? response?.data ?? response?.item ?? response?.transactionServiceOrder ?? response;
  }

  private mapFromDto(dto: any): TransactionServiceOrder {
    const groupId = dto?.GroupId ?? dto?.groupId ?? dto?.GroupID ?? dto?.groupID ?? dto?.DepartmentId ?? dto?.departmentId ?? null;
    const departmentId = dto?.DepartmentId ?? dto?.departmentId ?? null;
    const firstStatus = dto?.FirstStatus ?? dto?.firstStatus;
    const statusDirection = dto?.StatusDirection ?? dto?.statusDirection;
    return {
      id: dto?.Id ?? dto?.id ?? 0,
      configOrderId: dto?.ConfigOrderId ?? dto?.configOrderId ?? null,
      firstStatusId: dto?.FirstStatusId ?? dto?.firstStatusId ?? null,
      statusDirectionId: dto?.StatusDirectionId ?? dto?.statusDirectionId ?? null,
      groupId,
      firstStatusName: firstStatus?.Name ?? firstStatus?.name ?? null,
      statusDirectionName: statusDirection?.Name ?? statusDirection?.name ?? null,
      departmentId
    };
  }

  private normalizePayload(payload: TransactionServiceOrderCreate | TransactionServiceOrderUpdate): any {
    // O backend atual usa groupId; se vier apenas departmentId (legado), mantém.
    const anyPayload = payload as any;
    const groupId = anyPayload?.groupId ?? anyPayload?.GroupId ?? null;
    const departmentId = anyPayload?.departmentId ?? anyPayload?.DepartmentId ?? null;

    const normalized: any = { ...payload };
    if (groupId != null) {
      normalized.groupId = Number(groupId);
      delete normalized.departmentId;
      delete normalized.DepartmentId;
    } else if (departmentId != null) {
      normalized.departmentId = Number(departmentId);
    }
    return normalized;
  }
}
